/**
 * DM-7703: building blocks shared by the two website-lead tabs.
 * DM-8483: the account and consent filter rows and the account/contact
 * section of the lead drawer.
 * DM-8485: the outreach status cell.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, Search } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type {
  EarlyAccessLeadItem,
  WebsiteLeadOutreachSummary,
  WebsiteLeadSourceFilter,
} from '../../api/adminOps';
import { StatusChip } from '../../components/ui';
import {
  SEARCH_MAX_LENGTH,
  accountSummary,
  consentChip,
  consentEvidence,
  eligibilityChip,
  eligibilityHint,
  outreachChip,
  outreachSummaryLine,
} from '../websiteLeads.logic';

const SOURCE_OPTIONS: { value: WebsiteLeadSourceFilter; label: string }[] = [
  { value: 'all', label: 'All sources' },
  { value: 'website_form', label: 'Website' },
  { value: 'formspree_import', label: 'Formspree import' },
];

export const relative = (iso: string) => formatDistanceToNow(new Date(iso), { addSuffix: true });
export const absolute = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : null);

/** The search term once typing has paused, so each keystroke is not a privileged read. */
export function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p className="text-xs text-gray-text uppercase tracking-wide mb-1">{label}</p>
      <div className="text-sm text-white break-words">{value ?? '—'}</div>
    </div>
  );
}

export function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t border-gray-border pt-3 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-text/70">{title}</p>
      {children}
    </section>
  );
}

export function MailLink({ email }: { email: string }) {
  return (
    <a href={`mailto:${email}`} className="text-primary hover:underline break-all">
      {email}
    </a>
  );
}

export function SearchAndSource({
  search,
  onSearch,
  source,
  onSource,
  placeholder,
  idPrefix,
}: {
  search: string;
  onSearch: (value: string) => void;
  source: WebsiteLeadSourceFilter;
  onSource: (value: WebsiteLeadSourceFilter) => void;
  placeholder: string;
  idPrefix: string;
}) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="w-4 h-4 text-gray-text absolute left-3 top-1/2 -translate-y-1/2" />
        <label className="sr-only" htmlFor={`${idPrefix}-search`}>
          Search
        </label>
        <input
          id={`${idPrefix}-search`}
          type="search"
          className="input pl-9"
          placeholder={placeholder}
          maxLength={SEARCH_MAX_LENGTH}
          value={search}
          onChange={event => onSearch(event.target.value)}
        />
      </div>
      <label className="sr-only" htmlFor={`${idPrefix}-source`}>
        Source
      </label>
      <select
        id={`${idPrefix}-source`}
        className="input w-auto"
        value={source}
        onChange={event => onSource(event.target.value as WebsiteLeadSourceFilter)}
      >
        {SOURCE_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function FilterChip({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
        active ? 'bg-primary/20 text-primary' : 'bg-gray-500/10 text-gray-text hover:text-white'
      }`}
    >
      {label}
      {count !== undefined && <span className="ml-1.5 opacity-70">{count.toLocaleString()}</span>}
    </button>
  );
}

/** A labelled row of single-choice filter chips. */
export function ChipFilterGroup<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap" role="group" aria-label={label}>
      <span className="text-sm text-gray-text mr-2">{label}:</span>
      {options.map(option => (
        <FilterChip
          key={option.value}
          active={value === option.value}
          label={option.label}
          onClick={() => onChange(option.value)}
        />
      ))}
    </div>
  );
}

/** DM-8483: whether the lead signed up, and how they may be contacted. */
export function AccountAndContact({
  lead,
}: {
  lead: {
    has_account: boolean;
    account_created_at: string | null;
    contact_eligibility: string;
  };
}) {
  const chip = eligibilityChip(lead.contact_eligibility);
  const hint = eligibilityHint(lead.contact_eligibility);
  return (
    <DetailSection title="Account & contact">
      <DetailField label="DonateMate account" value={accountSummary(lead)} />
      <DetailField
        label="Contact"
        value={
          <div className="space-y-1">
            <StatusChip label={chip.label} tone={chip.tone} />
            {hint && <p className="text-xs text-gray-text">{hint}</p>}
          </div>
        }
      />
    </DetailSection>
  );
}

/** DM-8485: the outreach status chip with its one-line summary, for a table cell. */
export function OutreachCell({ summary }: { summary: WebsiteLeadOutreachSummary }) {
  const chip = outreachChip(summary);
  const line = outreachSummaryLine(summary);
  return (
    <div>
      <StatusChip label={chip.label} tone={chip.tone} />
      {line && <p className="text-xs text-gray-text mt-1 whitespace-nowrap">{line}</p>}
    </div>
  );
}

export function LoadError({
  what,
  error,
  retry,
}: {
  what: string;
  error: unknown;
  retry: () => void;
}) {
  return (
    <div className="glass-card p-6 border border-red-500/30 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-white font-medium mb-1">Failed to load {what}</p>
        <p className="text-sm text-gray-text mb-3">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <button onClick={retry} className="btn-secondary">
          Retry
        </button>
      </div>
    </div>
  );
}

export function ConsentDetail({ consent }: { consent: EarlyAccessLeadItem['marketing_consent'] }) {
  const chip = consentChip(consent);
  return (
    <DetailField
      label="Marketing consent"
      value={
        <div className="space-y-1">
          <StatusChip label={chip.label} tone={chip.tone} />
          {consentEvidence(consent) && (
            <p className="text-xs text-gray-text">{consentEvidence(consent)}</p>
          )}
        </div>
      }
    />
  );
}
